import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, SlidersHorizontal, X } from "lucide-react";
import { useDocstate } from "@/context/DocstateContext";
import { PropertyCard } from "@/components/docstate/PropertyCard";
import { reListings, PROPERTY_TYPES, cap } from "@/lib/docstate";

const SORTS = [
  { value: "newest", label: "Latest" },
  { value: "price_asc", label: "Price: Low to High" },
  { value: "price_desc", label: "Price: High to Low" },
  { value: "beds_desc", label: "Most Bedrooms" },
];
const BEDS = ["", "1", "2", "3", "4", "5+"];

export default function DocstateListings({ preset = {} }) {
  const data = useDocstate();
  const communities = data?.communities || [];
  const [params, setParams] = useSearchParams();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  const get = (k, d = "") => params.get(k) || d;
  const page = parseInt(get("page", "1"), 10) || 1;

  const update = (patch, resetPage = true) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => {
      if (v === "" || v == null) next.delete(k);
      else next.set(k, v);
    });
    if (resetPage) next.delete("page");
    setParams(next);
  };

  const load = useCallback(() => {
    setLoading(true);
    const q = {
      purpose: preset.purpose || get("purpose"), type: get("type"), community: get("community"),
      beds: get("beds"), min_price: get("min_price"), max_price: get("max_price"),
      q: get("q"), sort: get("sort", "newest"), page, limit: 9,
    };
    if (preset.holiday) q.holiday = "1";
    if (preset.status) q.status = preset.status;
    reListings(q).then(setResult).catch(() => setResult({ items: [], total: 0, pages: 1 })).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  useEffect(() => { load(); window.scrollTo(0, 0); }, [load]);

  const activeChips = [
    !preset.lockPurpose && get("purpose") && ["purpose", `For ${cap(get("purpose"))}`],
    get("type") && ["type", cap(get("type"))],
    get("community") && ["community", get("community")],
    get("beds") && ["beds", `${get("beds")} Beds`],
    get("min_price") && ["min_price", `≥ AED ${Number(get("min_price")).toLocaleString()}`],
    get("max_price") && ["max_price", `≤ AED ${Number(get("max_price")).toLocaleString()}`],
  ].filter(Boolean);

  const FilterControls = () => (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      <div>
        <label className="ds-eyebrow mb-1.5 block">Purpose</label>
        <select value={get("purpose")} onChange={(e) => update({ purpose: e.target.value })} data-testid="filter-purpose" disabled={preset.lockPurpose} className="ds-input disabled:opacity-50">
          <option value="">Buy & Rent</option><option value="sale">For Sale</option><option value="rent">For Rent</option>
        </select>
      </div>
      <div>
        <label className="ds-eyebrow mb-1.5 block">Type</label>
        <select value={get("type")} onChange={(e) => update({ type: e.target.value })} data-testid="filter-type" className="ds-input">
          <option value="">Any Type</option>{PROPERTY_TYPES.map((t) => <option key={t} value={t}>{cap(t)}</option>)}
        </select>
      </div>
      <div>
        <label className="ds-eyebrow mb-1.5 block">Community</label>
        <select value={get("community")} onChange={(e) => update({ community: e.target.value })} data-testid="filter-community" className="ds-input">
          <option value="">All Communities</option>{communities.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      <div>
        <label className="ds-eyebrow mb-1.5 block">Bedrooms</label>
        <select value={get("beds")} onChange={(e) => update({ beds: e.target.value })} data-testid="filter-beds" className="ds-input">
          {BEDS.map((b) => <option key={b} value={b}>{b ? `${b} Beds` : "Any"}</option>)}
        </select>
      </div>
      <div>
        <label className="ds-eyebrow mb-1.5 block">Min Price (AED)</label>
        <input type="number" value={get("min_price")} onChange={(e) => update({ min_price: e.target.value })} data-testid="filter-min-price" placeholder="0" className="ds-input" />
      </div>
      <div>
        <label className="ds-eyebrow mb-1.5 block">Max Price (AED)</label>
        <input type="number" value={get("max_price")} onChange={(e) => update({ max_price: e.target.value })} data-testid="filter-max-price" placeholder="Any" className="ds-input" />
      </div>
    </div>
  );

  return (
    <div className="ds-bg min-h-screen pt-16" data-testid="docstate-listings">
      <div className="mx-auto max-w-7xl px-4 pt-10 sm:px-8 lg:px-12">
        <p className="ds-eyebrow">{preset.eyebrow || "Properties in Dubai"}</p>
        <h1 className="ds-display mt-2 text-3xl font-medium text-white lg:text-4xl">
          {preset.title || (get("purpose") === "rent" ? "Properties for Rent" : get("purpose") === "sale" ? "Properties for Sale" : "All Properties")}
        </h1>
        {preset.subtitle && <p className="mt-2 max-w-2xl text-sm font-light text-[#9D8D83]">{preset.subtitle}</p>}

        {/* Filter bar */}
        <div className="sticky top-16 z-30 mt-6 -mx-4 border-y border-[#C9A24B]/15 bg-[#0B0B0C]/95 px-4 py-3 backdrop-blur-lg sm:mx-0 sm:rounded-xl sm:border sm:px-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <button onClick={() => setShowFilters((v) => !v)} data-testid="toggle-filters" className="ds-outline-btn px-4 py-2 text-xs">
              <SlidersHorizontal className="h-4 w-4" /> Filters {activeChips.length ? `(${activeChips.length})` : ""}
            </button>
            <div className="flex items-center gap-3">
              <span className="text-xs text-[#9D8D83]" data-testid="results-count">{result ? `${result.total} results` : "…"}</span>
              <select value={get("sort", "newest")} onChange={(e) => update({ sort: e.target.value }, false)} data-testid="filter-sort" className="ds-input !w-auto !py-2 text-xs">
                {SORTS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          </div>
          {activeChips.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {activeChips.map(([k, label]) => (
                <button key={k} onClick={() => update({ [k]: "" })} className="inline-flex items-center gap-1 rounded-full border border-[#C9A24B]/30 bg-[#C9A24B]/10 px-3 py-1 text-[11px] text-[#F3E5AB]">
                  {label} <X className="h-3 w-3" />
                </button>
              ))}
              <button onClick={() => setParams(new URLSearchParams())} data-testid="clear-filters" className="text-[11px] text-[#9D8D83] underline hover:text-[#F3E5AB]">Clear all</button>
            </div>
          )}
          {showFilters && <div className="mt-4" data-testid="filter-panel"><FilterControls /></div>}
        </div>

        {/* Results */}
        <div className="py-10">
          {loading ? (
            <div className="flex min-h-[40vh] items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#C9A24B]" /></div>
          ) : result && result.items.length ? (
            <>
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 lg:gap-8">
                {result.items.map((l, i) => <PropertyCard key={l.id} listing={l} index={i} />)}
              </div>
              {result.pages > 1 && (
                <div className="mt-12 flex items-center justify-center gap-2" data-testid="pagination">
                  {Array.from({ length: result.pages }, (_, i) => i + 1).map((p) => (
                    <button key={p} onClick={() => update({ page: p === 1 ? "" : String(p) }, false)} data-testid={`page-${p}`}
                      className={`h-10 w-10 rounded-full text-sm font-medium transition-colors ${p === page ? "bg-gradient-to-r from-[#C9A24B] to-[#B8912E] text-[#0B0B0C]" : "border border-[#C9A24B]/30 text-[#C9BBB2] hover:bg-[#C9A24B]/10"}`}>
                      {p}
                    </button>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className="ds-card py-16 text-center" data-testid="no-results">
              <p className="ds-display text-xl text-[#F3E5AB]">No properties match your search</p>
              <p className="mt-2 text-sm text-[#9D8D83]">Try widening your filters or clearing them.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
